import { useEffect, useState } from "react";
import app from "nystem";
import { Wrapper, Input, Button, useUser } from "nystem-components";
import { watchSecretsStatus } from "./status";

const SecretsUnlock = ({ children }) => {
  const user = useUser();
  const isSuper = [].concat(user?.role || []).includes("super");
  const [state, setState] = useState(false);
  const status = state?.sessionid === user?.sessionid ? state.status : false;
  const setStatus = (status) => setState({ sessionid: user.sessionid, status });
  const [passphrase, setPassphrase] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [account, setAccount] = useState(() => `master@${window.location.host}`);

  useEffect(() => {
    if (!isSuper) return;

    return watchSecretsStatus(app.connection, user.sessionid, (status) => {
      setState({ sessionid: user.sessionid, status });
      if (!status) setDismissed(false);
    });
  }, [isSuper, user?.sessionid]);

  if (!isSuper || status?.locked === false) return children || null;
  if (!status)
    return <Wrapper className="p-4 text-gray-400">Checking server passwords…</Wrapper>;
  if (dismissed)
    return (
      <Wrapper className="p-4 text-gray-400">
        Server passwords are locked.
        <Button className="ml-2" size="sm" onClick={() => setDismissed(false)}>Unlock</Button>
      </Wrapper>
    );

  const firstTime = !status.initialized;

  const submit = async (ev) => {
    ev?.preventDefault();
    if (busy || !passphrase) return;
    if (firstTime && passphrase !== confirm) {
      setError("Passphrases do not match");
      return;
    }
    setBusy(true);
    const reply = await app.connection.emit({
      type: "secrets",
      action: "unlock",
      passphrase,
      sessionid: user.sessionid,
    });
    setBusy(false);

    // Offer to save it like a login, only once it is known to be right
    if (!reply.error && window.PasswordCredential)
      navigator.credentials
        .store(new window.PasswordCredential({ id: account, password: passphrase, name: "Master passphrase" }))
        .catch(() => {});

    setPassphrase("");
    setConfirm("");
    setError(reply.error || "");
    if (!reply.error) setStatus(reply);
  };

  return (
    <Wrapper className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <form onSubmit={submit} method="post" autoComplete="on" className="w-full max-w-md rounded border border-gray-700 bg-gray-900 p-6 text-sm">
        <Wrapper renderAs="h2" className="mb-2 text-lg font-semibold">
          {firstTime ? "Set master passphrase" : "Unlock server passwords"}
        </Wrapper>
        <Wrapper renderAs="p" className="mb-4 text-gray-400">
          {firstTime
            ? "Stored server passwords are encrypted with this passphrase. It is asked for after every restart and cannot be recovered, so keep a copy somewhere safe."
            : "The server was restarted. Enter the master passphrase to use stored server passwords."}
        </Wrapper>
        {/* Shaped like a login so password managers can save and fill it */}
        <Input
          type="text"
          name="username"
          className="mb-2 w-full"
          placeholder="Account"
          autoComplete="username"
          value={account}
          onChange={setAccount}
        />
        <Input
          type="password"
          name="password"
          className="mb-2 w-full"
          placeholder="Passphrase"
          autoComplete={firstTime ? "new-password" : "current-password"}
          value={passphrase}
          onChange={setPassphrase}
          focus
        />
        {firstTime && (
          <Input
            type="password"
            className="mb-2 w-full"
            name="confirm-password"
            placeholder="Repeat passphrase"
            autoComplete="new-password"
            value={confirm}
            onChange={setConfirm}
          />
        )}
        {error && (
          <Wrapper renderAs="p" className="mb-2 text-red-400">
            {error}
          </Wrapper>
        )}
        {/* Primary button first so Enter submits instead of dismissing */}
        <Wrapper className="mt-4 flex flex-row-reverse justify-start gap-2">
          {/* Native submit so password managers see a real form submission */}
          <button
            type="submit"
            disabled={busy || !passphrase}
            className="rounded-full bg-blue-500 px-2 py-1 text-sm text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-blue-400"
          >
            {busy ? "Unlocking..." : firstTime ? "Set passphrase" : "Unlock"}
          </button>
          <Button type="secondary" size="sm" onClick={() => setDismissed(true)}>
            Later
          </Button>
        </Wrapper>
      </form>
    </Wrapper>
  );
};

export default SecretsUnlock;
